/*
PROV: COHINT001.BF.OYUDQET0HP
REQ: SYS-ARCH-15
WHY: Frontend application module.
*/

import { ApiEnvelope, ApiError, humanizeKnownIdsInText } from "@cohorta/shared/api/client";
import { getIdToken } from "@cohorta/shared/auth/firebase";

import { sitePath } from "./sitePrefix";

function formatValidationDetail(details: unknown): string {
  if (!Array.isArray(details) || details.length === 0) return "";
  const first = details[0] as any;
  const loc = Array.isArray(first?.loc) ? first.loc.filter(Boolean).join(".") : "";
  const msg = typeof first?.msg === "string" ? first.msg : "";
  if (loc && msg) return `${loc}: ${msg}`;
  return msg || "";
}

function newCorrelationId(): string | undefined {
  try {
    // Browser-supported in modern Chromium/Firefox/Safari.
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // ignore
  }
  return undefined;
}

async function requestJson<T>(path: string, options: RequestInit): Promise<T> {
  const correlationId = newCorrelationId();
  const token = typeof window !== "undefined" ? await getIdToken() : null;
  const resp = await fetch(path, {
    ...options,
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(correlationId ? { "x-correlation-id": correlationId } : {}),
      ...(options.headers || {}),
    },
    credentials: "include",
  });

  const contentType = resp.headers.get("content-type") || "";
  const requestIdHeader = resp.headers.get("x-request-id");
  if (!contentType.toLowerCase().includes("application/json")) {
    const text = await resp.text().catch(() => "");
    const code = resp.status === 401 ? "unauthorized" : "unexpected";
    const requestId = requestIdHeader || correlationId;
    const msg = humanizeKnownIdsInText(text || "Request failed") + ` (${code})` + (requestId ? ` [req ${requestId}]` : "");
    const error: ApiError = new Error(msg);
    error.code = code;
    error.status = resp.status;
    if (requestId) error.requestId = requestId;
    throw error;
  }

  const envelope = (await resp.json()) as ApiEnvelope<T>;
  if (!resp.ok || !envelope?.ok) {
    const code = envelope?.error?.code || (resp.status === 401 ? "unauthorized" : "unexpected");
    const requestId = envelope?.meta?.request_id || requestIdHeader || correlationId || undefined;
    let message = envelope?.error?.message || "Request failed";
    if (code === "validation_error") {
      const detail = formatValidationDetail(envelope?.error?.details);
      if (detail) message = `${message} - ${detail}`;
    }
    message = humanizeKnownIdsInText(message);
    const msg = message + ` (${code})` + (requestId ? ` [req ${requestId}]` : "");
    const error: ApiError = new Error(msg);
    error.code = code;
    error.status = resp.status;
    error.requestId = requestId;

    // Defence-in-depth: any admin endpoint that fires `tcs_not_accepted`
    // means the backend rejected the request because the caller has not
    // accepted (or is on an outdated version of) the platform T&Cs. Send
    // them to the acceptance page rather than surfacing a raw 403.
    if (code === "tcs_not_accepted" && typeof window !== "undefined") {
      const onTcsAccept = window.location.pathname.endsWith("/tcs-accept");
      if (!onTcsAccept) {
        window.location.assign(sitePath("/tcs-accept"));
      }
    }
    throw error;
  }
  return envelope.data as T;
}

export async function getJson<T>(path: string): Promise<T> {
  return requestJson<T>(path, { method: "GET" });
}

export async function postJson<T, B = unknown>(path: string, body: B, init?: RequestInit): Promise<T> {
  return requestJson<T>(path, { method: "POST", body: JSON.stringify(body || {}), ...init });
}

export async function putJson<T, B = unknown>(path: string, body: B): Promise<T> {
  return requestJson<T>(path, { method: "PUT", body: JSON.stringify(body || {}) });
}

export async function patchJson<T, B = unknown>(path: string, body: B): Promise<T> {
  return requestJson<T>(path, { method: "PATCH", body: JSON.stringify(body || {}) });
}

export async function deleteJson<T>(path: string): Promise<T> {
  // Send an empty JSON body. `requestJson` always sets `content-type:
  // application/json`, and the Fastify backend rejects a request that declares
  // that content-type with NO body (400 FST_ERR_CTP_EMPTY_JSON_BODY). Matches
  // the postJson/putJson/patchJson helpers, which always serialise a body.
  // (cohorta-group-admin#44)
  return requestJson<T>(path, { method: "DELETE", body: JSON.stringify({}) });
}

/**
 * DELETE with a JSON body. Most deletes carry no payload (`deleteJson`), but the
 * event-cancel endpoint (cohorta#505) accepts `{ reason }`, so this variant
 * serialises a body the same way `postJson`/`patchJson` do.
 */
export async function deleteJsonWithBody<T, B = unknown>(path: string, body: B): Promise<T> {
  return requestJson<T>(path, { method: "DELETE", body: JSON.stringify(body || {}) });
}

/**
 * Resolve an auth-gated media path to a browser-displayable src — the web
 * equivalent of the mobile media service (NetworkImageOrSvg).
 *
 * `GET /api/v1/media/...` requires a Bearer token, which an `<img>` tag cannot
 * send, so we fetch it here with auth. Deployed environments return the signed
 * GCS URL as JSON (`Accept: application/json`) which we hand straight to `<img>`
 * (https → CSP `img-src https:`); the storage emulator proxies the raw bytes,
 * which we wrap in an object URL (`blob:` → CSP `img-src blob:`). Requesting
 * JSON avoids following a cross-origin 302 (which `connect-src` would block).
 *
 * Returns the src plus an optional `revoke()` to free any created object URL.
 */
export async function fetchMediaSrc(path: string): Promise<{ src: string; revoke?: () => void }> {
  const token = typeof window !== "undefined" ? await getIdToken() : null;
  const resp = await fetch(path, {
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      accept: "application/json",
    },
    credentials: "include",
  });

  if (!resp.ok) {
    const error: ApiError = new Error(`Media request failed (${resp.status})`);
    error.status = resp.status;
    throw error;
  }

  const contentType = (resp.headers.get("content-type") || "").toLowerCase();
  if (contentType.includes("application/json")) {
    const envelope = (await resp.json()) as ApiEnvelope<{ signedUrl?: string }>;
    const signedUrl = envelope?.data?.signedUrl;
    if (!signedUrl) throw new Error("Media response missing signedUrl");
    return { src: signedUrl };
  }

  // Emulator path: the endpoint streamed the bytes directly.
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  return { src: url, revoke: () => URL.revokeObjectURL(url) };
}

/** Upload a raw file (avatar image). Returns envelope.data. */
export async function uploadFile<T>(path: string, file: File): Promise<T> {
  const correlationId = newCorrelationId();
  const token = typeof window !== "undefined" ? await getIdToken() : null;
  const resp = await fetch(path, {
    method: "POST",
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(correlationId ? { "x-correlation-id": correlationId } : {}),
      "content-type": file.type || "application/octet-stream",
      "x-filename": file.name,
    },
    body: file,
    credentials: "include",
  });

  const contentType = resp.headers.get("content-type") || "";
  const requestIdHeader = resp.headers.get("x-request-id");
  if (!contentType.toLowerCase().includes("application/json")) {
    const text = await resp.text().catch(() => "");
    const code = resp.status === 401 ? "unauthorized" : "unexpected";
    const requestId = requestIdHeader || correlationId;
    const msg = humanizeKnownIdsInText(text || "Upload failed") + ` (${code})` + (requestId ? ` [req ${requestId}]` : "");
    const error: ApiError = new Error(msg);
    error.code = code;
    error.status = resp.status;
    if (requestId) error.requestId = requestId;
    throw error;
  }

  const envelope = (await resp.json()) as ApiEnvelope<T>;
  if (!resp.ok || !envelope?.ok) {
    const code = envelope?.error?.code || "unexpected";
    const requestId = envelope?.meta?.request_id || requestIdHeader || correlationId || undefined;
    const message = humanizeKnownIdsInText(envelope?.error?.message || "Upload failed");
    const msg = message + ` (${code})` + (requestId ? ` [req ${requestId}]` : "");
    const error: ApiError = new Error(msg);
    error.code = code;
    error.status = resp.status;
    error.requestId = requestId;
    throw error;
  }
  return envelope.data as T;
}
